import React, { ReactElement, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { StepIndicator } from '@ver-uds/uswds-react';
import { useAuthState } from '@vis-auth/react';
import { EverifyUserRole } from '@vis-auth/vis-user-client';
import { Account, AccountType } from 'eve-account-client';
import ReactGA from 'react-ga4';
import { EnrollmentContext } from '../../context/Enrollment/EnrollmentContext';
import { useAccountQuery } from '../../services';
import FullWidthLayout from '../../layouts/FullWidthLayout';

import EnrollmentChecklist from './sub-pages/EnrollmentChecklist';
import AccessMethod from './sub-pages/AccessMethod';
import EmployerCategory from './sub-pages/EmployerCategory';
import CompanyInformation from './sub-pages/CompanyInformation';
import CompanyAddresses from './sub-pages/CompanyAddresses';
import HiringSites from './sub-pages/HiringSites';
import EmployerEverifyUsers from './sub-pages/EVerifyUsers/EmployerEverifyUsers';
import CorporateAdminEverifyUsers from './sub-pages/EVerifyUsers/CorporateAdminEverifyUsers';
import ClientEverifyUsers from './sub-pages/EVerifyUsers/ClientEverifyUsers';
import ChildEverifyUsers from './sub-pages/EVerifyUsers/ChildEverifyUsers';
import ReviewSubmit from './sub-pages/ReviewSubmit';
import EnrollmentCompleted from './sub-pages/EnrollmentCompleted';

import { EnrollmentData, EnrollmentType } from './EnrollmentData';
import SubPageName from './SubPageName';
import { useGlobalData } from '../../context/Global/GlobalContext';
import EnrollmentExitModal from '../../components/Enrollment/EnrollmentExitModal';
import EmployerEverifyUsersMFA from './sub-pages/EVerifyUsers/EmployerEverifyUsersMFA';
import ChildEverifyUsersMFA from './sub-pages/EVerifyUsers/ChildEverifyUsersMFA';
import { useEnvironment } from '../../context/Environment/EnvironmentContext';
import CorporateAdminEverifyUsersMFA from './sub-pages/EVerifyUsers/CorporateAdminEverifyUsersMFA';
import { useToggles } from '../../context/Toggles/TogglesContext';

type EnrollmentSubPage = {
  name: SubPageName;
  component: ReactElement;
  label: string;
};

export type EnrollmentProps = {
  /**
   * Type of enrollment.
   *
   * For most enrollments, this will be `EnrollmentType.INITIAL`.
   * For a corporate company location (child employer) enrollment, this should be `EnrollmentType.CHILD`.
   * For an employer agent's client enrollment, this should be `EnrollmentType.CLIENT`.
   */
  enrollmentType?: EnrollmentType;
};

const Enrollment = ({ enrollmentType = EnrollmentType.INITIAL }: EnrollmentProps): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(0);
  const [activeEditPage, setActiveEditPage] = useState<SubPageName | null>(null);
  const [completedEnrollment, setCompletedEnrollment] = useState<Account | null>(null);
  const [enrollmentData, setEnrollmentData] = useState<EnrollmentData>({
    enrollmentType,
    defaultHiringSiteCreated: false,
  });

  const { CITIZENSHIP_NONCITIZEN_AUTHORIZED_TO_WORK } = useEnvironment();

  const toggles = useToggles();
  const isMfaEnvironment = toggles?.get('MFA_USER_CREATION') === 'on';

  const { user } = useAuthState();
  const history = useHistory();
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id as string;
  const { data: accountData, isLoading } = useAccountQuery(accountId, { useErrorBoundary: false });
  const { setIsOnEnrollment } = useGlobalData();

  // Enrollment Exit Modal
  const [requestedPath, setRequestedPath] = useState<string | null>(null);
  const [authorizedEnrollmentExit, setAuthorizedEnrollmentExit] = useState(false);

  const nawToAawToggle = CITIZENSHIP_NONCITIZEN_AUTHORIZED_TO_WORK ? 'noncitizen' : 'alien';

  useEffect(() => {
    if (user?.roles.includes(EverifyUserRole.ENROLLMENT) && accountData?.status) {
      setIsOnEnrollment(false);
      setCompletedEnrollment(accountData);
    } else if (!completedEnrollment) {
      setIsOnEnrollment(true);
    }
  }, [user, accountData, setIsOnEnrollment, completedEnrollment]);

  const handleExitEnrollment = () => {
    // This is only here because handle.block insists.
  };

  /** Exit Enrollment:
   * Triggers "Enrollment Warning Modal" for user confirmation.
   * Redirects once user confirmation received.
   */
  useEffect(() => {
    if (requestedPath && authorizedEnrollmentExit) {
      history.push(requestedPath);
    }
    const unblock = history.block(({ pathname }) => {
      if (!completedEnrollment && !authorizedEnrollmentExit) {
        setRequestedPath(pathname);
        return false;
      }
      return handleExitEnrollment();
    });
    return () => {
      unblock();
    };
  }, [history, requestedPath, authorizedEnrollmentExit, completedEnrollment]);

  const getEverifyUsersComponent = (): JSX.Element => {
    if (isMfaEnvironment) {
      switch (enrollmentType) {
        case EnrollmentType.CHILD:
          return <ChildEverifyUsersMFA />;

        case EnrollmentType.CLIENT:
          return <ClientEverifyUsers />;

        case EnrollmentType.INITIAL:
        default:
          if (enrollmentData.accessMethodFormData?.type === AccountType.CORPORATE) {
            return <CorporateAdminEverifyUsersMFA />;
          }
          return <EmployerEverifyUsersMFA />;
      }
    }
    switch (enrollmentType) {
      case EnrollmentType.CHILD:
        return <ChildEverifyUsers />;

      case EnrollmentType.CLIENT:
        return <ClientEverifyUsers />;

      case EnrollmentType.INITIAL:
      default:
        if (enrollmentData.accessMethodFormData?.type === AccountType.CORPORATE) {
          return <CorporateAdminEverifyUsers />;
        }
        return <EmployerEverifyUsers />;
    }
  };

  const getEverifyUsersLabel = (): string => {
    if (enrollmentType === EnrollmentType.CLIENT) {
      return 'Signatory ';
    }
    if (isMfaEnvironment && enrollmentData.accessMethodFormData?.type === AccountType.CORPORATE) {
      return 'Corporate Administrator';
    }
    return 'E-Verify Users ';
  };

  const introductoryPages: EnrollmentSubPage[] = [
    { name: SubPageName.ENROLLMENT_CHECKLIST, label: 'Enrollment Checklist', component: <EnrollmentChecklist /> },
    { name: SubPageName.ACCESS_METHOD, label: 'Choose Account Access', component: <AccessMethod /> },
  ].filter(({ name }) => {
    if (enrollmentData.enrollmentType === EnrollmentType.CLIENT) {
      return false;
    }
    switch (name) {
      case SubPageName.ENROLLMENT_CHECKLIST:
        return enrollmentData.enrollmentType === EnrollmentType.INITIAL;
      default:
        return true;
    }
  });

  const corePages: EnrollmentSubPage[] = [
    { name: SubPageName.EMPLOYER_CATEGORY, label: 'Employer Category ', component: <EmployerCategory /> },
    { name: SubPageName.COMPANY_INFORMATION, label: 'Company Information ', component: <CompanyInformation /> },
    { name: SubPageName.COMPANY_ADDRESSES, label: 'Company Addresses ', component: <CompanyAddresses /> },
    { name: SubPageName.HIRING_SITES, label: 'Hiring Sites ', component: <HiringSites /> },
    { name: SubPageName.EVERIFY_USERS, label: getEverifyUsersLabel(), component: getEverifyUsersComponent() },
    {
      name: SubPageName.REVIEW_AND_SUBMIT,
      label: `Review ${isMfaEnvironment ? 'and' : '&'} Submit `,
      component: <ReviewSubmit nawToAawToggle={nawToAawToggle} />,
    },
  ].filter(({ name }) => {
    switch (name) {
      case SubPageName.EMPLOYER_CATEGORY:
      case SubPageName.HIRING_SITES:
        return enrollmentData.accessMethodFormData?.type !== AccountType.CORPORATE;
      default:
        return true;
    }
  });

  const pages = [...introductoryPages, ...corePages];

  const nextPage = () => {
    const onLastPage = pageNumber === pages.length - 1;

    if (activeEditPage) {
      setActiveEditPage(null);
    } else if (!onLastPage) {
      setPageNumber(pageNumber + 1);
    }
  };

  const previousPage = () => {
    const onFirstPage = pageNumber === 0;

    if (activeEditPage) {
      setActiveEditPage(null);
    } else if (!onFirstPage) {
      setPageNumber(pageNumber - 1);
    }
  };

  const labels = corePages.map((page) => page.label);
  const stepIndex = corePages.indexOf(pages[pageNumber]);

  const showStepper = !completedEnrollment && !activeEditPage && corePages.indexOf(pages[pageNumber]) >= 0;

  let pageTitle: string;
  switch (enrollmentType) {
    case EnrollmentType.CHILD:
      pageTitle = 'Add New Company Location';
      break;
    case EnrollmentType.CLIENT:
      pageTitle = 'Add New Client';
      break;
    case EnrollmentType.INITIAL:
    default:
      pageTitle = 'E-Verify Enrollment';
      break;
  }

  // Sets up descriptive Page Title for screen readers (508)
  const activePageLabel = activeEditPage
    ? pages.find((page) => page.name === activeEditPage)?.label
    : pages[pageNumber].label;
  const fullPageTitle = activePageLabel ? `${pageTitle} - ${activePageLabel}` : pageTitle;

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Enrollment',
    });
  }, []);

  return (
    <FullWidthLayout title={fullPageTitle} headerTitle={pageTitle}>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <section className={`${activeEditPage ? 'padding-bottom-4' : 'padding-y-4'}`}>
          {showStepper && (
            <StepIndicator
              labels={labels}
              stepIndex={stepIndex}
              counters="small"
              className="margin-0 position-relative z-0"
            />
          )}
          <EnrollmentContext.Provider
            value={{
              enrollmentData,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              loggedInAccount: accountData!,
              setEnrollmentData,
              pageNumber,
              nextPage,
              previousPage,
              totalPages: pages.length,
              activeEditPage,
              setActiveEditPage,
              completedEnrollment,
              setCompletedEnrollment,
            }}
          >
            {completedEnrollment ? (
              <EnrollmentCompleted />
            ) : (
              <>
                {activeEditPage
                  ? pages.find((page) => page.name === activeEditPage)?.component
                  : pages[pageNumber].component}
              </>
            )}
          </EnrollmentContext.Provider>
          <EnrollmentExitModal
            when={requestedPath !== null && !completedEnrollment && !authorizedEnrollmentExit}
            onOK={() => setAuthorizedEnrollmentExit(true)}
            onCancel={() => setRequestedPath(null)}
          />
        </section>
      )}
    </FullWidthLayout>
  );
};

export default Enrollment;
